import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { EmptyState } from '@/shared/components';
import {
  useApi,
  type ContractResponse,
  type PropertyFileResponse,
  type PropertyFileTreeNode,
  type SupplierInvoiceResponse,
} from '@/shared/api';
import { AkteBrowserLayout } from './AkteBrowserLayout';
import { AkteBulkActionsHost } from './AkteBulkActionsHost';
import { AkteDialogHost } from './AkteDialogHost';
import { AkteFolderMenu } from './AkteFolderMenu';
import { AkteHeader } from './AkteHeader';
import { findFileById } from './akteBrowserNav';
import { isCustomFolder } from './akteFolderRules';
import { decideDrop, suggestKindForFolder } from './akteReclassifyRules';
import { topFolderForPath } from './akteFolderMeta';
import { buildAkteSummary } from './akteSummary';
import type { FileActions } from './akteFileActions';
import { useAkteBrowser } from './useAkteBrowser';
import { useAkteBulkActions } from './useAkteBulkActions';
import { useAkteBulkDialogs, type AkteBulkHandlers } from './useAkteBulkDialogs';
import { useAkteDialogs } from './useAkteDialogs';
import { useAkteFileDownload } from './useAkteFileDownload';
import { useAkteMutations } from './useAkteMutations';
import { useAkteUndoableActions } from './useAkteUndoableActions';
import { useAkteUploads } from './useAkteUploads';
import { useFileDropGuard } from './useFileDropGuard';

export function PropertyAkteWorkspace({ propertyId }: { propertyId: string }) {
  const api = useApi();
  const uploads = useAkteUploads(propertyId);
  const mutations = useAkteMutations(propertyId);
  const undoable = useAkteUndoableActions(propertyId);
  const browser = useAkteBrowser();
  const dialogs = useAkteDialogs();
  const bulkDialogs = useAkteBulkDialogs();
  const bulk = useAkteBulkActions(propertyId);
  const download = useAkteFileDownload(propertyId);
  useFileDropGuard();

  const [showTrash, setShowTrash] = useState(false);
  const [previewFile, setPreviewFile] = useState<PropertyFileResponse | null>(null);
  const [editFile, setEditFile] = useState<PropertyFileResponse | null>(null);
  const [editContract, setEditContract] = useState<ContractResponse | null>(null);
  const [editInvoice, setEditInvoice] = useState<SupplierInvoiceResponse | null>(null);

  const tree = useQuery({
    queryKey: ['propertyFiles', 'tree', propertyId],
    queryFn: () => api.propertyFiles.tree(propertyId),
  });
  const trash = useQuery({
    queryKey: ['propertyFiles', 'trash', propertyId],
    queryFn: () => api.propertyFiles.trash(propertyId),
  });

  const summary = useMemo(() => buildAkteSummary(tree.data), [tree.data]);
  const { selectedIds } = browser.selection;
  const selectedFiles = useMemo(() => {
    if (!tree.data) return [];
    return [...selectedIds]
      .map((id) => findFileById(tree.data!, id))
      .filter((f): f is PropertyFileResponse => f !== null);
  }, [tree.data, selectedIds]);

  const previewById = (fileId: string) => {
    const f = tree.data ? findFileById(tree.data, fileId) : null;
    if (f) setPreviewFile(f);
  };
  const downloadById = (fileId: string) => {
    const f = tree.data ? findFileById(tree.data, fileId) : null;
    if (f) download(f);
  };

  const actions: FileActions = {
    onPreview: setPreviewFile,
    onDownload: download,
    onEdit: setEditFile,
    onDelete: dialogs.openDeleteFile,
    onMove: dialogs.openMove,
    onReclassify: dialogs.openReclassify,
    // Drag-Move ist wie Drag-Trash/-Reclassify still + Undo (B0) — nicht der Menü-Move (mit Dialog).
    onMoveTo: undoable.moveWithUndo,
  };

  // Cross-Category-Drop: platz-stabiles Ziel-Kind → still + Undo; sonst Reclassify-Dialog auf die
  // Ziel-Kategorie vorbelegt. Die Achsen-Entscheidung liegt in decideDrop (rein, getestet).
  const onReclassifyDrop = (file: PropertyFileResponse, targetFolder: string) => {
    const decision = decideDrop(file.folder, targetFolder);
    if (decision.action === 'silent') {
      void undoable.reclassifyWithUndo(file, decision.kind);
    } else if (decision.action === 'dialog') {
      dialogs.openReclassify(file, decision.kind);
    }
  };

  const bulkHandlers: AkteBulkHandlers = {
    onMove: bulkDialogs.openMove,
    onReclassify: bulkDialogs.openReclassify,
    onDelete: bulkDialogs.openDelete,
    onDownload: bulk.download,
  };

  // Drag einer Mehrfach-Auswahl (>1): das Ziel bestimmt die Bulk-Achse. Move ist direkt (Ziel steht
  // fest); Reclassify + Delete laufen über die bestehenden Bulk-Dialoge (Bestätigung, kein Bulk-Undo).
  const bulkDrag = {
    selectedFiles,
    onMove: (files: PropertyFileResponse[], target: string) => void bulk.move(files, target),
    onTrash: (files: PropertyFileResponse[]) => bulkDialogs.openDelete(files),
    onReclassify: (files: PropertyFileResponse[], target: string) =>
      bulkDialogs.openReclassify(files, suggestKindForFolder(target)),
  };

  const folderMenu = (node: PropertyFileTreeNode) =>
    isCustomFolder(node.path) ? (
      <AkteFolderMenu
        onNewSubfolder={() => dialogs.openNewFolder(node.path)}
        onRename={() => dialogs.openRenameFolder(node.path)}
        onDelete={() => dialogs.openDeleteFolder(node.path)}
      />
    ) : null;

  // In einem manuellen (Custom-)Ordner landet der Upload dort; sonst kanonische Kind-Ablage.
  const uploadInto = (path: string) =>
    uploads.route(topFolderForPath(path), [], isCustomFolder(path) ? path : undefined);

  return (
    <>
      <AkteHeader
        summary={summary}
        onNewFolder={() => dialogs.openNewFolder(browser.folderPath)}
        onUpload={() => uploadInto(browser.folderPath)}
      />

      {tree.isLoading ? (
        <p className="text-ink-faint mt-6">Lade Akte…</p>
      ) : tree.error ? (
        <EmptyState
          icon="Error"
          title="Akte konnte nicht geladen werden"
          description="Bitte prüfen Sie den Zugriff oder kehren Sie zur Aktenübersicht zurück."
        />
      ) : !tree.data ? (
        <EmptyState
          icon="Error"
          title="Akte konnte nicht geladen werden"
          description="Bitte versuchen Sie es erneut oder kehren Sie zur Aktenübersicht zurück."
        />
      ) : (
        <AkteBrowserLayout
          propertyId={propertyId}
          tree={tree.data}
          trash={{ data: trash.data, isLoading: trash.isLoading }}
          browser={browser}
          showTrash={showTrash}
          onOpenTrash={() => setShowTrash(true)}
          onNavigate={(p) => {
            setShowTrash(false);
            browser.navigateTo(p);
          }}
          actions={actions}
          selectedFiles={selectedFiles}
          bulk={bulkHandlers}
          folderMenu={folderMenu}
          onUploadInto={uploadInto}
          onTrashDrop={undoable.deleteWithUndo}
          onReclassifyDrop={onReclassifyDrop}
          bulkDrag={bulkDrag}
          fachansicht={{
            onPreviewFileId: previewById,
            onDownloadFileId: downloadById,
            onPreviewFile: setPreviewFile,
            onDownloadFile: download,
            onEditContract: setEditContract,
            onEditInvoice: setEditInvoice,
          }}
        />
      )}

      <AkteDialogHost
        propertyId={propertyId}
        tree={tree.data}
        dialogs={dialogs}
        mutations={mutations}
        uploads={uploads}
        edit={{
          previewFile,
          editFile,
          editContract,
          editInvoice,
          closePreview: () => setPreviewFile(null),
          closeEditFile: () => setEditFile(null),
          closeEditContract: () => setEditContract(null),
          closeEditInvoice: () => setEditInvoice(null),
        }}
        onDownload={download}
      />

      {tree.data && (
        <AkteBulkActionsHost
          tree={tree.data}
          dialogs={bulkDialogs}
          bulk={bulk}
          onCreateSubfolder={dialogs.openNewFolder}
        />
      )}
    </>
  );
}

export function AkteRegisterPage() {
  const { id: propertyId } = useParams({ from: '/_admin/properties/$id/akte' });
  return <PropertyAkteWorkspace propertyId={propertyId} />;
}
